import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader, Form, FormGroup, Label, Col, Input, Button, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import { OrderActions, OrderStore } from 'Stores/orderStore'
import { Redirect } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Component } from 'reflux'
import useDashboardData from 'Components/dashboard/useDashboardData.js'

const AreaSeperator = styled(FormGroup)`
  border-bottom: 1px dashed #ddd;
`

export default class CustomOrder extends Component {
  constructor(props) {
    super(props)
    this.store = OrderStore
  }
  render() {
    return (
      <React.Fragment>
        <CustomOrderForm />
      </React.Fragment>
    )
  }
}

function CustomOrderForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [orderStatus, setOrderStatus] = useState()
  const [isRedirect, setIsRedirect] = useState(false)
  const [errorInfo, setErrorInfo] = useState('')
  const [data] = useDashboardData('schemas/allbrands')
  const [userBalance, setUserBalance] = useState('')
  const [userDeliveryInformation, setUserDeliveryInformation] = useState(null)

  // Form values
  const [selectedBrand, setSelectedBrand] = useState(undefined)
  const [userIdentifier, setUserIdentifier] = useState(null)
  const [orderlineTitle, setOrderlineTitle] = useState('')
  const [orderComment, setOrderComment] = useState('')
  const [orderAmountPoints, setOrderAmountPoints] = useState(0)
  const [orderlineProductPriceWithTax, setOrderlineProductPriceWithTax] = useState(0)
  const [orderlineProductPrice, setOrderlineProductPrice] = useState(0)
  const [orderMarkup, setOrderMarkup] = useState(0)
  const [profitPercentage, setProfitPercentage] = useState(0)
  const [userCustomerNumber, setUserCustomerNumber] = useState('')

  const numberFormatter = new Intl.NumberFormat('da-DK')

  const calculateMarkup = (purchasePrice, sellingPoints, brandRatio) => {
    return (((sellingPoints / brandRatio - purchasePrice) / purchasePrice) * 100).toFixed(2)
  }

  const calculateProfitPercentage = markup => {
    const m = markup / 100
    return (markup / (1 + m)).toFixed(2)
  }

  const handleChangeBrand = brandId => {
    const brand = data ? data.find(b => b.BrandID === brandId) : undefined
    if (brand) {
      setSelectedBrand(brand)
      handleMarkupUpdate(brand.personalShopperMarkup, brand)
    }
  }

  const handlePurchasePriceUpdate = price => {
    if (price > 0 && selectedBrand && selectedBrand.brandPointRatio) {
      setOrderAmountPoints((price * (1 + orderMarkup / 100) * selectedBrand.brandPointRatio).toFixed(0))
    }
    setOrderlineProductPrice(price)
    setOrderlineProductPriceWithTax(price > 0 ? (price * 1.25).toFixed(2) : price)
  }

  const handlePurchasePriceWithTaxUpdate = price => {
    handlePurchasePriceUpdate(price > 0 ? (price * 0.8).toFixed(2) : price)
    setOrderlineProductPriceWithTax(price)
  }

  const handleSellingPointsUpdate = points => {
    if (orderlineProductPrice > 0 && points > 0 && selectedBrand && selectedBrand.brandPointRatio) {
      const markup = calculateMarkup(orderlineProductPrice, points, selectedBrand.brandPointRatio)
      setOrderMarkup(markup)
      setProfitPercentage(calculateProfitPercentage(markup))
    }
    setOrderAmountPoints(points)
  }

  const handleMarkupUpdate = (markup, brand) => {
    if (orderlineProductPrice > 0 && brand && brand.brandPointRatio) {
      const sellingPoints = orderlineProductPrice * (1 + markup / 100) * brand.brandPointRatio
      setOrderAmountPoints(sellingPoints.toFixed(0))
    }
    setOrderMarkup(markup)
    setProfitPercentage(calculateProfitPercentage(markup))
  }

  const handleProfitPercentageUpdate = profit => {
    if (orderlineProductPrice > 0 && selectedBrand && selectedBrand.brandPointRatio) {
      const sellingPoints = (orderlineProductPrice * selectedBrand.brandPointRatio) / (1 - profit / 100)
      setOrderAmountPoints(sellingPoints.toFixed(0))
      setOrderMarkup(calculateMarkup(orderlineProductPrice, sellingPoints, selectedBrand.brandPointRatio))
    } else {
      setOrderMarkup(0)
    }
    setProfitPercentage(profit)
  }

  useEffect(() => {
    if (orderStatus === 'SUCCESS') {
      toast.success('Created custom order', {
        autoClose: 5000
      })
      setIsRedirect(true)
    }
    if (orderStatus === 'FAILED') {
      toast.error('Failed to create custom order: ' + errorInfo, {
        autoClose: 5000
      })
      setOrderStatus()
    }
  }, [orderStatus])

  useEffect(() => {
    const unsubscribe = OrderActions.createCustomOrder.failed.listen(error => {
      setIsLoading(false)
      if (error && error.message) {
        setErrorInfo(error.message)
      }
      setOrderStatus('FAILED')
    })
    return () => {
      unsubscribe()
    }
  })

  useEffect(() => {
    const unsubscribe = OrderActions.getUserBalance.failed.listen(error => {
      setIsLoading(false)
      if (error && error.message) {
        setErrorInfo(error.message)
      }
      setOrderStatus('FAILED')
    })
    return () => {
      unsubscribe()
    }
  })

  if (isRedirect) {
    return <Redirect to={'/orders'} />
  }

  function onGetInformationClick() {
    getUserBalance()
    getUserDeliveryInformation()
  }

  function getUserBalance() {
    if (selectedBrand && userIdentifier) {
      setIsLoading(true)
      OrderActions.getUserBalance.completed.listen(e => {
        setIsLoading(false)
        setUserBalance(e)
      })
      OrderActions.getUserBalance(selectedBrand.BrandID, userIdentifier, userCustomerNumber)
    }
  }

  function getUserDeliveryInformation() {
    if (selectedBrand && userIdentifier) {
      OrderActions.getUserDeliveryInformation.completed.listen(e => {
        setUserDeliveryInformation(e)
      })
      OrderActions.getUserDeliveryInformation(selectedBrand.BrandID, userIdentifier, userCustomerNumber)
    }
  }

  function handleFormSubmit(event) {
    event.preventDefault()
    setIsLoading(true)
    OrderActions.createCustomOrder.completed.listen(() => {
      setIsLoading(false)
      setOrderStatus('SUCCESS')
    })
    const order = {
      brandIdentifier: selectedBrand.BrandID.toString(),
      userIdentifier,
      orderlineTitle,
      orderComment,
      orderAmountPoints,
      orderlineProductPrice,
      userCustomerNumber
    }
    OrderActions.createCustomOrder(order)
  }
  return (
    <Card>
      <CardHeader>Custom Order </CardHeader>
      <CardBody>
        <Form onSubmit={e => handleFormSubmit(e)}>
          <FormGroup row>
            <Label for='brandIdentifier' sm={2}>
              Brand
            </Label>
            <Col sm={4}>
              <Input
                type='select'
                name='brandIdentifier'
                value={selectedBrand ? selectedBrand.BrandID : 0}
                onChange={e => handleChangeBrand(+e.target.value)}
                required={true}
              >
                <option value=''>Select Brand</option>
                {data &&
                  data.length &&
                  data
                    .sort(function(a, b) {
                      if (a.brandName < b.brandName) {
                        return -1
                      }
                      if (a.brandName > b.brandName) {
                        return 1
                      }
                      return 0
                    })
                    .map(e => {
                      return (
                        <option value={e.BrandID} key={e.BrandID}>
                          {e.brandName} ({e.BrandID})
                        </option>
                      )
                    })}
              </Input>
            </Col>
            <Label sm={4}>{selectedBrand ? `Point ratio: ${selectedBrand.brandPointRatio}` : ''}</Label>
          </FormGroup>
          <FormGroup row>
            <Label for='userIdentifier' sm={2}>
              User Email or Phone
            </Label>
            <Col sm={4}>
              <Input
                type='text'
                name='userIdentifier'
                id='userIdentifier'
                placeholder='User Email or Phone'
                onChange={e => setUserIdentifier(e.target.value)}
                value={userIdentifier}
                required={true}
              />
            </Col>
          </FormGroup>
          {selectedBrand && selectedBrand.BrandID === 87 ? (
            <FormGroup row>
              <Label sm={2} for='userCustomerNumber'>
                Customer number
              </Label>
              <Col sm={10}>
                <Input
                  type='text'
                  name='userCustomerNumber'
                  id='userCustomerNumber'
                  placeholder='Customer number'
                  onChange={e => setUserCustomerNumber(e.target.value)}
                  value={userCustomerNumber}
                  required={false}
                />
              </Col>
            </FormGroup>
          ) : null}
          <FormGroup row>
            <Label sm={2}>User balance:</Label>
            <Label sm={2}>
              {userBalance
                ? `${numberFormatter.format(userBalance.activePoints)} (${numberFormatter.format(userBalance.waitingPoints)} waiting)`
                : 'Not found'}
            </Label>
            <Col sm={8}>
              <Button onClick={onGetInformationClick}>Get information</Button>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={2}>Delivery information:</Label>
            <Label sm={8}>
              {userDeliveryInformation ? (
                <>
                  <p>{userDeliveryInformation.company}</p>
                  <p>{userDeliveryInformation.name}</p>
                  <p>{userDeliveryInformation.address}</p>
                  <p>{`${userDeliveryInformation.zipcode} ${userDeliveryInformation.city}`}</p>
                  <p>{userDeliveryInformation.phone}</p>
                  <p>{userDeliveryInformation.email}</p>
                </>
              ) : (
                <p>{'Not found'}</p>
              )}
            </Label>
          </FormGroup>
          <AreaSeperator row />
          <FormGroup row>
            <Label for='orderlineTitle' sm={2}>
              Title
            </Label>
            <Col sm={10}>
              <Input
                type='text'
                name='orderlineTitle'
                id='orderlineTitle'
                placeholder='Title'
                onChange={e => setOrderlineTitle(e.target.value)}
                value={orderlineTitle}
                required={true}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for='orderComment' sm={2}>
              Note
            </Label>
            <Col sm={10}>
              <Input
                type='text'
                name='orderComment'
                id='orderComment'
                placeholder='Custom order'
                onChange={e => setOrderComment(e.target.value)}
                value={orderComment}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for='orderlineProductPriceWithTax' sm={2}>
              Purchase Price (with tax)
            </Label>
            <Col sm={4}>
              <InputGroup>
                <InputGroupAddon addonType='prepend'>
                  <InputGroupText>kr</InputGroupText>
                </InputGroupAddon>
                <Input
                  type='number'
                  name='orderlineProductPriceWithTax'
                  id='orderlineProductPriceWithTax'
                  placeholder='Price'
                  onChange={e => handlePurchasePriceWithTaxUpdate(e.target.value)}
                  value={orderlineProductPriceWithTax}
                  required={true}
                />
              </InputGroup>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for='orderlineProductPrice' sm={2}>
              Purchase Price (no tax)
            </Label>
            <Col sm={4}>
              <InputGroup>
                <InputGroupAddon addonType='prepend'>
                  <InputGroupText>kr</InputGroupText>
                </InputGroupAddon>
                <Input
                  type='number'
                  name='orderlineProductPrice'
                  id='orderlineProductPrice'
                  placeholder='Price'
                  onChange={e => handlePurchasePriceUpdate(e.target.value)}
                  value={orderlineProductPrice}
                  required={true}
                />
              </InputGroup>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for='orderAmountPoints' sm={2}>
              Selling price (points)
            </Label>
            <Col sm={4}>
              <InputGroup>
                <InputGroupAddon addonType='prepend'>
                  <InputGroupText>pt</InputGroupText>
                </InputGroupAddon>
                <Input
                  type='number'
                  name='orderAmountPoints'
                  id='orderAmountPoints'
                  placeholder='Points'
                  onChange={e => handleSellingPointsUpdate(e.target.value)}
                  value={orderAmountPoints}
                  required={true}
                  style={
                    selectedBrand && orderAmountPoints / selectedBrand.brandPointRatio < selectedBrand.personalShopperMinimumPrice
                      ? { color: 'red' }
                      : {}
                  }
                />
              </InputGroup>
            </Col>
            <Label for='orderAmountPoints' sm={6}>
              {selectedBrand
                ? `${(orderAmountPoints / selectedBrand.brandPointRatio).toFixed(2)} kr. (Brand minimum: ${
                    selectedBrand.personalShopperMinimumPrice
                  } kr.)`
                : ''}
            </Label>
          </FormGroup>
          <FormGroup row>
            <Label for='orderMarkup' sm={2}>
              Markup
            </Label>
            <Col sm={4}>
              <InputGroup>
                <InputGroupAddon addonType='prepend'>
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>
                <Input
                  type='number'
                  name='orderMarkup'
                  id='orderMarkup'
                  placeholder='Price'
                  onChange={e => handleMarkupUpdate(e.target.value, selectedBrand)}
                  value={orderMarkup}
                  required={true}
                  style={selectedBrand && orderMarkup < selectedBrand.personalShopperMarkup ? { color: 'red' } : {}}
                />
              </InputGroup>
            </Col>
            <Label for='orderAmountPoints' sm={6}>
              {selectedBrand ? `Brand markup: ${selectedBrand.personalShopperMarkup}%` : ''}
            </Label>
          </FormGroup>
          <FormGroup row>
            <Label for='orderlineProfitPercentage' sm={2}>
              Profit percentage
            </Label>
            <Col sm={4}>
              <InputGroup>
                <InputGroupAddon addonType='prepend'>
                  <InputGroupText>%</InputGroupText>
                </InputGroupAddon>
                <Input
                  type='number'
                  name='orderlineProfitPercentage'
                  id='orderlineProfitPercentage'
                  placeholder='Price'
                  onChange={e => handleProfitPercentageUpdate(e.target.value)}
                  value={profitPercentage}
                  required={true}
                />
              </InputGroup>
            </Col>
          </FormGroup>
          <AreaSeperator row />
          <FormGroup row>
            <Col sm={{ size: 10, offset: 2 }}>
              <Button disabled={isLoading}>{isLoading ? <FontAwesomeIcon icon='spinner' spin /> : 'Opret & underret'}</Button>
            </Col>
          </FormGroup>
        </Form>
      </CardBody>
    </Card>
  )
}
